import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { Editor, EditorContent, EditorMenuBar } from "tiptap";
import { Link, HardBreak, Heading, Underline, BulletList, ListItem } from "tiptap-extensions";
import { BBold, IItalic } from "../rich-text-single-line-editor/components/extensions";
import { GLUtil } from "commonui";
import { Icon } from "ant-design-vue";

export enum RTEItems {
	bold = 0,
	italic = 1,
	underline = 2,
	bulletList = 3,
	link = 4,
	hardBreak = 5,
	listItem = 6,
	heading = 7
}
@Component({
	components: { EditorContent, EditorMenuBar, Icon }
})
export default class RichTextEditor extends Vue {
	@Prop({ default: null }) value!: string;
	@Prop({ default: null }) defaultValue!: string;
	@Prop({ default: false }) allowEdit!: boolean;
	@Prop({ default: false }) autoFocus!: boolean | "end" | "start" | number | null;
	@Prop({ default: false }) showMenu!: boolean;
	@Prop({ default: () => [RTEItems.bold, RTEItems.italic, RTEItems.underline] }) menuItems!: RTEItems[]; // menu items visible
	@Prop({ default: () => [] }) allowedExtensions!: RTEItems[]; // text editor supported extensions
	@Prop({ default: null }) beforePasteValueUpdate!: (value: string, prevValue: string) => string; // only before pasting

	public itemEnum = RTEItems;
	public editorControl: Editor = null;

	/**
	 * Variable to denote that 'beforePasteValueUpdate' should be called.
	 * @member
	 * @private
	 * @see onEditorPaste
	 * @see onEditorUpdate
	 * @type {boolean}
	 */
	private processPaste: boolean = false;

	/**
	 * stores previous values from editor (Used while user pastes)
	 * @member
	 * @private
	 * @type {string}
	 */
	private previousValue: string = "";

	/**
	 * extensions that will be used to render the content by default
	 * @member
	 * @type {RTEItems[]}
	 * @see getExtensions
	 */
	private defaultExtensions: RTEItems[] = [
		RTEItems.bold,
		RTEItems.italic,
		RTEItems.underline,
		RTEItems.link,
		RTEItems.hardBreak,
		RTEItems.heading
	];

	created() {
		this.initEditor();
	}

	@Watch("value")
	onValueChanged() {
		Vue.nextTick(() => {
			if (!GLUtil.isNullOrUndefined(this.value) && this.value.localeCompare(this.editorControl.getHTML())) {
				this.editorControl.setContent(this.value);
			}
		});
	}

	beforeDestroy() {
		if (this.editorControl != null) {
			this.editorControl.destroy();
		}
	}

	/**
	 * Initializes rich text editor
	 * @function
	 * @private
	 */
	initEditor() {
		this.editorControl = new Editor({
			editable: this.allowEdit,
			autoFocus: this.autoFocus,
			content: this.defaultValue || this.value || "",
			extensions: this.getExtensions(),
			enableInputRules: [BulletList],
			enableCopyPasteRules: [BulletList],
			onBlur: () => {
				let value = (this.editorControl.getHTML() as string) || "";
				value = this.sanitize(value);
				this.$emit("blur", value);
			},
			onUpdate: this.onEditorUpdate,
			onPaste: this.onEditorPaste
		});

		// To set the cursor at the end when auto focused initially.
		this.$nextTick(() => {
			this.editorControl.setContent(this.defaultValue || this.value || "");
		});
	}

	/**
	 * Callback for editor update event.
	 * @callback
	 * @param d Object of editor
	 */
	onEditorUpdate(d: any) {
		Vue.nextTick(() => {
			// get value and do basic senitization
			let value: string = d.getHTML() || "";
			value = this.sanitize(value);

			//run 'beforeValueUpdate' function from parent component if provided
			if (this.beforePasteValueUpdate && this.processPaste) {
				value = this.beforePasteValueUpdate(value, this.previousValue);
				this.processPaste = false;
				// set the updated content
				this.editorControl.setContent(value);
			}
			this.previousValue = value;
			// emit events for antd form
			this.$emit("update:value", value);
			this.$emit("change", { target: { value } });
			this.$emit("input", value);
		});
	}

	/**
	 * Callback for editor paste event.
	 * @callback
	 */
	onEditorPaste() {
		this.previousValue = this.editorControl.getHTML();
		this.processPaste = true;
	}

	/**
	 * Removes empty 'p' tags
	 * @param value
	 * @returns
	 */
	sanitize(value: string): string {
		// remove p tag, if only that is present
		return value.replace(/^<p><\/p>/g, "").trim();
	}

	isMenuItemVisible(item: RTEItems) {
		return this.menuItems.some((s) => s === item);
	}

	/**
	 * Returns the object of extensions, according to prop 'allowedExtensions'
	 * @returns {RTEItems[]}
	 */
	getExtensions(): RTEItems[] {
		const ext = !this.allowedExtensions.length ? this.defaultExtensions : this.allowedExtensions;
		const result: any[] = [];
		ext.map((m) => {
			switch (m) {
				case RTEItems.bold:
					result.push(new BBold());
					break;
				case RTEItems.italic:
					result.push(new IItalic());
					break;
				case RTEItems.underline:
					result.push(new Underline());
					break;
				case RTEItems.link:
					result.push(new Link());
					break;
				case RTEItems.heading:
					result.push(new Heading());
					break;
				case RTEItems.hardBreak:
					result.push(new HardBreak());
					break;
				case RTEItems.listItem:
					result.push(new ListItem());
					break;
				case RTEItems.bulletList:
					result.push(new BulletList());
					break;
			}
		});
		return result;
	}

	/**
	 * Other Editors
	 * https://www.npmjs.com/package/react-draft-wysiwyg
	 * https://awesomeopensource.com/projects/wysiwyg-editor
	 * https://awesomeopensource.com/project/ueberdosis/tiptap
	 * https://github.com/ueberdosis/tiptap
	 */
}
